import { Modal } from "react-bootstrap";
import { Select } from "antd";
import {
  ClassOption,
  CreateEventPayload,
  EMPTY_FORM,
  EVENT_TYPES,
  EventType,
  TYPE_COLORS,
} from ".";
import { createEvent } from "../../api/event";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import withPermission from "../../core/common/autorisations/withPermission";
import {
  Action,
  buildPermission,
  Resource,
} from "../../core/common/autorisations/permissionsMap";

interface CreateModalProps {
  show: boolean;
  availableClasses: ClassOption[];
  onSuccess: () => void;
  onClose: () => void;
}

const CreateEventModal: React.FC<CreateModalProps> = ({
  show,
  availableClasses,
  onClose,
  onSuccess,
}) => {
  const [form, setForm] = useState<CreateEventPayload>(EMPTY_FORM);
  const [errors, setErrors] = useState<
    Partial<Record<keyof CreateEventPayload, string>>
  >({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (show) {
      setForm(EMPTY_FORM);
      setErrors({});
    }
  }, [show]);

  const validate = (): boolean => {
    const e: typeof errors = {};
    // console.log("Data: ",form)
    if (!form.type) e.type = "Le type est obligatoire.";
    if (!form.title.trim()) e.title = "Le titre est obligatoire.";
    if (!form.start_date) e.start_date = "La date de début est obligatoire.";
    if (!form.end_date) e.end_date = "La date de fin est obligatoire.";
    if (
      form.start_date &&
      form.end_date &&
      new Date(form.start_date) > new Date(form.end_date)
    ) {
      e.end_date = "La date de fin doit être après la date de début.";
    }
    if (!form.all_school && form.id_classes.length === 0) {
      e.id_classes = "Sélectionnez au moins une classe.";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await createEvent(form);
      onSuccess();
      onClose();
      toast.success("Creation effectuee avec succes !");
    } catch (error: any) {
      // console.log(
      //       "Erreur lors de la création de l'événement: ",
      //       error
      //     );
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Erreur lors de la création de l'événement, réessayez plus tard",
      );
    } finally {
      setLoading(false);
    }
  };

  const toggleClass = (id: string) => {
    setForm((f: any) => ({
      ...f,
      id_classes: f.id_classes.includes(id)
        ? f.id_classes.filter((c: any) => c !== id)
        : [...f.id_classes, id],
    }));
  };

  if (!show) return null;

  return (
    <Modal show={show} onHide={onClose} size="lg" centered>
      <Modal.Header>
        <Modal.Title>Créer un événement</Modal.Title>
        <button
          type="button"
          className="btn btn-sm btn-danger"
          onClick={onClose}
        >
          x
        </button>
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit} noValidate>
          {/* Type preview strip */}
          {form.type && (
            <div
              style={{
                height: 4,
                borderRadius: 6,
                background: TYPE_COLORS[form.type].bg,
                marginBottom: "1.25rem",
                transition: "background 0.3s",
              }}
            />
          )}

          {/* Type + Titre */}
          <div className="row g-3 mb-3">
            <div className="col-md-5">
              <label className="form-label">
                Type <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <select
                className={`form-select${errors.type ? " is-invalid" : ""}`}
                value={form.type}
                onChange={(e) =>
                  setForm((f: any) => ({
                    ...f,
                    type: e.target.value as EventType,
                  }))
                }
              >
                <option value="">Sélectionner un type</option>
                {EVENT_TYPES.map((t: any) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              {errors.type && (
                <div className="invalid-feedback d-block">{errors.type}</div>
              )}
            </div>
            <div className="col-md-7">
              <label className="form-label">
                Titre <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <input
                type="text"
                className={`form-control${errors.title ? " is-invalid" : ""}`}
                placeholder="Ex : Vacances de Noël"
                value={form.title}
                onChange={(e) =>
                  setForm((f: any) => ({ ...f, title: e.target.value }))
                }
              />
              {errors.title && (
                <div className="invalid-feedback d-block">{errors.title}</div>
              )}
            </div>
          </div>

          {/* Dates */}
          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <label className="form-label">
                Date de début <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <input
                type="date"
                className={`form-control${errors.start_date ? " is-invalid" : ""}`}
                value={form.start_date}
                onChange={(e) =>
                  setForm((f: any) => ({ ...f, start_date: e.target.value }))
                }
              />
              {errors.start_date && (
                <div className="invalid-feedback d-block">
                  {errors.start_date}
                </div>
              )}
            </div>
            <div className="col-md-6">
              <label className="form-label">
                Date de fin <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <input
                type="date"
                className={`form-control${errors.end_date ? " is-invalid" : ""}`}
                value={form.end_date}
                min={form.start_date}
                onChange={(e) =>
                  setForm((f: any) => ({ ...f, end_date: e.target.value }))
                }
              />
              {errors.end_date && (
                <div className="invalid-feedback d-block">
                  {errors.end_date}
                </div>
              )}
            </div>
          </div>

          {/* Description */}
          <div className="mb-3">
            <label className="form-label">Description</label>
            <textarea
              className="form-control"
              rows={2}
              placeholder="Détails supplémentaires (optionnel)"
              value={form.description}
              onChange={(e) =>
                setForm((f: any) => ({ ...f, description: e.target.value }))
              }
            />
          </div>

          {/* Audience toggle */}
          <div className="mb-3">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="create_all_school"
                checked={form.all_school}
                onChange={(e) =>
                  setForm((f: any) => ({
                    ...f,
                    all_school: e.target.checked,
                    id_classes: e.target.checked ? [] : f.id_classes,
                  }))
                }
              />
              <label className="form-check-label" htmlFor="create_all_school">
                <i
                  className="fa fa-building me-2"
                  style={{ color: form.all_school ? "#4f46e5" : "#94a3b8" }}
                />
                Concerne tout l'établissement
              </label>
            </div>
          </div>

          {/* Class selection */}
          {!form.all_school && (
            <div className="mb-3">
              <label className="form-label">
                Classes concernées <span style={{ color: "#ef4444" }}>*</span>
              </label>
              {errors.id_classes && (
                <div className="invalid-feedback d-block mb-2">
                  <i className="fa fa-exclamation-circle me-1" />
                  {errors.id_classes}
                </div>
              )}
              {availableClasses.length === 0 ? (
                <p style={{ color: "#94a3b8", fontSize: "0.82rem", margin: 0 }}>
                  Aucune classe disponible.
                </p>
              ) : (
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="Sélectionner une ou plusieurs classes"
                  value={form.id_classes}
                  onChange={(values) =>
                    setForm((f: any) => ({
                      ...f,
                      id_classes: values as string[],
                    }))
                  }
                  style={{ width: "100%" }}
                  getPopupContainer={(trigger: any) =>
                    trigger.parentElement as HTMLElement
                  }
                  dropdownStyle={{ zIndex: 2000 }}
                  options={availableClasses.map((cls: any) => ({
                    label: cls.name,
                    value: cls.id,
                  }))}
                />
              )}
              {form.id_classes.length > 0 && (
                <small
                  style={{
                    color: "#6366f1",
                    marginTop: "0.35rem",
                    display: "block",
                  }}
                >
                  <i className="fa fa-check-circle me-1" />
                  {form.id_classes.length} classe(s) sélectionnée(s)
                </small>
              )}
            </div>
          )}
        </form>
      </Modal.Body>
      <Modal.Footer className="gap-2">
        <button
          type="button"
          className="btn btn-light"
          onClick={onClose}
          disabled={loading}
        >
          Annuler
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={loading}
          onClick={handleSubmit}
        >
          {loading ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              />
              Création…
            </>
          ) : (
            <>
              <i className="fa fa-plus-circle me-2" />
              Créer l'événement
            </>
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default withPermission(
  CreateEventModal,
  [buildPermission(Resource.EVENT, Action.CREATE)],
  "masquer",
);
